import { Platform, StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { GROUP_INFO_SHEET_AVATAR_SIZE } from './GroupInfoSheet.constants';

const PRIZE_COLOR_GOLD = '#FFCB47';
const PRIZE_COLOR_SILVER = '#C0C0C0';
const PRIZE_COLOR_BRONZE = '#CD7F32';

export const getGroupInfoSheetStyles = (theme: Theme) =>
  StyleSheet.create({
    backdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.background.overlay,
    },
    root: {
      flex: 1,
      justifyContent: 'flex-end',
    },
    sheet: {
      backgroundColor: theme.background.card,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      borderWidth: 1,
      borderColor: theme.gray[200],
      borderBottomWidth: 0,
      maxHeight: '90%',
      paddingBottom: Platform.OS === 'ios' ? 36 : 20,
    },
    handle: {
      width: 36,
      height: 4,
      borderRadius: 2,
      backgroundColor: theme.gray[300],
      alignSelf: 'center',
      marginTop: 12,
      marginBottom: 2,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 12,
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 14,
      borderBottomWidth: 1,
      borderBottomColor: theme.gray[200],
    },
    headerBadge: {
      width: 48,
      flexShrink: 0,
      alignItems: 'center',
      marginTop: 2,
    },
    headerBadgeLogo: {
      width: 52,
      height: 52,
      borderRadius: 12,
      overflow: 'hidden',
      backgroundColor: theme.gray[100],
      marginTop: 0,
    },
    headerLogoImage: {
      width: 52,
      height: 52,
    },
    headerInfo: {
      flex: 1,
      minWidth: 0,
    },
    headerName: {
      fontSize: 17,
      fontWeight: '800',
      color: theme.gray[900],
      letterSpacing: -0.3,
    },
    headerMeta: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[600],
      marginTop: 2,
    },
    headerDescription: {
      fontSize: 12,
      color: theme.gray[500],
      marginTop: 4,
      lineHeight: 17,
    },
    scrollContent: {
      paddingBottom: 8,
    },
    section: {
      marginTop: 20,
    },
    sectionHeader: {
      flexDirection: 'row',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      marginBottom: 10,
    },
    sectionTitle: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.primary,
      letterSpacing: 1.4,
      textTransform: 'uppercase',
    },
    membersRow: {
      flexDirection: 'row',
      gap: 10,
      paddingHorizontal: 16,
      alignItems: 'flex-start',
    },
    memberItem: {
      alignItems: 'center',
      gap: 5,
      width: GROUP_INFO_SHEET_AVATAR_SIZE + 8,
    },
    memberAvatar: {
      width: GROUP_INFO_SHEET_AVATAR_SIZE,
      height: GROUP_INFO_SHEET_AVATAR_SIZE,
    },
    memberName: {
      fontSize: 10,
      fontWeight: '600',
      color: theme.gray[800],
      textAlign: 'center',
      maxWidth: GROUP_INFO_SHEET_AVATAR_SIZE + 8,
      lineHeight: 13,
    },
    prizePoolRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      marginHorizontal: 16,
      paddingHorizontal: 14,
      paddingVertical: 10,
      borderRadius: 10,
      marginBottom: 6,
      backgroundColor: theme.background.cardElevated,
      borderWidth: 1,
      borderColor: theme.accent + '33',
    },
    prizePoolPlaceLabel: {
      flex: 1,
      fontSize: 13,
      fontWeight: '800',
      letterSpacing: 0.3,
    },
    prizePoolPlaceLabelGold: { color: PRIZE_COLOR_GOLD },
    prizePoolPlaceLabelSilver: { color: PRIZE_COLOR_SILVER },
    prizePoolPlaceLabelBronze: { color: PRIZE_COLOR_BRONZE },
    prizePoolCoinRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
    },
    prizePoolCoin: {
      width: 20,
      height: 20,
    },
    prizePoolAmount: {
      fontSize: 16,
      fontWeight: '900',
      color: theme.accent,
      letterSpacing: -0.3,
      fontVariant: ['tabular-nums'],
    },
    prizePoolLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[600],
      marginTop: 1,
    },
    descriptionBox: {
      marginHorizontal: 16,
      padding: 14,
      borderRadius: 12,
      backgroundColor: theme.background.cardElevated,
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    descriptionText: {
      fontSize: 13,
      fontWeight: '400',
      color: theme.gray[700],
      lineHeight: 20,
    },
    inviteBox: {
      marginHorizontal: 16,
      padding: 14,
      borderRadius: 14,
      backgroundColor: theme.primary + '10',
      borderWidth: 1,
      borderColor: theme.primary + '33',
    },
    inviteCodeLabel: {
      fontSize: 9,
      fontWeight: '800',
      color: theme.primary,
      letterSpacing: 1.2,
      textTransform: 'uppercase',
    },
    inviteCode: {
      fontSize: 22,
      fontWeight: '900',
      color: theme.gray[900],
      letterSpacing: 4,
      marginTop: 4,
      fontVariant: ['tabular-nums'],
    },
    inviteActions: {
      flexDirection: 'row',
      gap: 8,
      marginTop: 12,
    },
    inviteActionBtn: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 10,
      borderRadius: 10,
      backgroundColor: theme.gray[100],
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    inviteActionBtnPrimary: {
      backgroundColor: theme.primary,
      borderColor: theme.primary,
    },
    inviteActionBtnPressed: {
      opacity: 0.75,
    },
    inviteActionBtnHover: {
      opacity: 0.9,
    },
    inviteActionBtnText: {
      fontSize: 12,
      fontWeight: '800',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: theme.gray[700],
    },
    inviteActionBtnTextPrimary: {
      color: theme.onPrimary,
    },
    prizeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingHorizontal: 16,
      paddingVertical: 10,
      borderRadius: 10,
      marginHorizontal: 16,
      marginBottom: 6,
      backgroundColor: theme.background.cardElevated,
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    prizeMedal: {
      fontSize: 22,
      lineHeight: 26,
    },
    prizeInfo: {
      flex: 1,
    },
    prizeRowBorderGold: { borderColor: PRIZE_COLOR_GOLD + '44' },
    prizeRowBorderSilver: { borderColor: PRIZE_COLOR_SILVER + '44' },
    prizeRowBorderBronze: { borderColor: PRIZE_COLOR_BRONZE + '44' },
    prizePlaceLabel: {
      fontSize: 9,
      fontWeight: '800',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    prizePlaceLabelGold: { color: PRIZE_COLOR_GOLD },
    prizePlaceLabelSilver: { color: PRIZE_COLOR_SILVER },
    prizePlaceLabelBronze: { color: PRIZE_COLOR_BRONZE },
    prizeDescription: {
      fontSize: 13,
      fontWeight: '700',
      color: theme.gray[800],
      marginTop: 1,
    },
    dangerZone: {
      marginHorizontal: 16,
      marginTop: 24,
    },
    leaveButton: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      paddingVertical: 13,
      borderRadius: 12,
      backgroundColor: theme.error + '14',
      borderWidth: 1,
      borderColor: theme.error + '44',
    },
    leaveButtonPressed: {
      opacity: 0.75,
    },
    leaveButtonHover: {
      opacity: 0.9,
    },
    leaveButtonLabel: {
      fontSize: 13,
      fontWeight: '800',
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: theme.error,
    },
    leaveNote: {
      marginTop: 8,
      fontSize: 11,
      color: theme.gray[500],
      textAlign: 'center',
    },
    headerNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      minWidth: 0,
    },
    headerEditPencilBtn: {
      padding: 3,
      flexShrink: 0,
    },
    headerEditPencilBtnPressed: {
      opacity: 0.6,
    },
    headerEditPencilBtnHover: {
      opacity: 0.75,
    },
    headerNameInput: {
      fontSize: 17,
      fontWeight: '800',
      color: theme.gray[900],
      letterSpacing: -0.3,
      paddingVertical: 4,
      paddingHorizontal: 8,
      marginBottom: 6,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.primary + '66',
      backgroundColor: theme.primary + '0A',
      ...(Platform.OS === 'web' && {
        outlineStyle: 'none' as never,
      }),
    },
    headerEditActions: {
      flexDirection: 'row',
      gap: 8,
    },
    headerEditActionBtn: {
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: theme.gray[100],
      borderWidth: 1,
      borderColor: theme.gray[200],
    },
    headerEditActionBtnPrimary: {
      backgroundColor: theme.primary,
      borderColor: theme.primary,
    },
    headerEditActionBtnPressed: {
      opacity: 0.75,
    },
    headerEditActionBtnHover: {
      opacity: 0.9,
    },
    headerEditActionBtnDisabled: {
      opacity: 0.55,
    },
    headerEditActionBtnText: {
      fontSize: 11,
      fontWeight: '800',
      letterSpacing: 0.3,
      color: theme.gray[700],
    },
    headerEditActionBtnTextPrimary: {
      color: theme.onPrimary,
    },
  });
